import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldAlert, Play, Undo2, Lock, ChevronLeft, ChevronRight, WifiOff, Wifi, FileText, XCircle, CheckCircle2, Trophy, ArrowLeftRight, HeartCrack, Timer } from "lucide-react";
import {
  getCachedMatch, saveCachedMatch, runAction, isOnline, pendingCount,
  localApplyPoint, localApplyUndo, localApplyUndoCompletion, localApplyWalkover, localApplyInjury, localApplyTimeout, localApplyToss, localApplyStart, localApplyScore,
  flushQueue,
} from "@/lib/refOffline";

const ROUND_LABELS = { group: "Group", r32: "R32", r16: "R16", qf: "QF", sf: "SF", "3rd_place": "3rd Place", final: "Final" };
const EVT_LABELS = { mens_singles: "M-Singles", mens_doubles: "M-Doubles", womens_singles: "W-Singles", womens_doubles: "W-Doubles", mixed_doubles: "Mixed" };
const ageLbl = (a) => (a === "19+" ? "Open (19+)" : a || "—");
const isDoubles = (m) => (m?.event_category || "").includes("doubles");
const splitDoubles = (name) => (name || "").split(" / ").map((s) => s.trim()).filter(Boolean);

// =========================================================================
// Player / Team label card — big, readable, doubles-aware
// =========================================================================
function TeamCard({ name, doubles, size = "lg", tone = "default", extra }) {
  const parts = doubles ? splitDoubles(name) : [name || "TBD"];
  const font = size === "xl" ? "text-3xl" : size === "lg" ? "text-2xl" : "text-lg";
  const border = tone === "primary" ? "border-jpa-primary bg-jpa-surface" : tone === "muted" ? "border-jpa-border" : "border-jpa-border bg-white";
  return (
    <div className={`border-2 ${border} rounded-sm p-3 text-center transition-colors`}>
      {parts.length === 2 ? (
        <div className="space-y-0.5">
          <div className={`font-display tracking-wider truncate ${font === "text-3xl" ? "text-2xl" : "text-xl"}`}>{parts[0]}</div>
          <div className="text-jpa-muted text-[10px] font-bold uppercase tracking-widest">+</div>
          <div className={`font-display tracking-wider truncate ${font === "text-3xl" ? "text-2xl" : "text-xl"}`}>{parts[1]}</div>
        </div>
      ) : (
        <div className={`font-display tracking-wider truncate ${font}`}>{parts[0]}</div>
      )}
      {extra}
    </div>
  );
}

// =========================================================================
// Manual score grid — fallback for missed taps
// =========================================================================
function ManualScoreGrid({ match, onSave, onCancel }) {
  const bo = match.best_of || 1;
  const [games, setGames] = useState(() => {
    const existing = match.games?.length ? match.games : [];
    return Array.from({ length: bo }, (_, i) => existing[i] || [0, 0]);
  });
  const setCell = (idx, side, val) => {
    const v = Math.max(0, parseInt(val) || 0);
    setGames((g) => g.map((row, i) => i === idx ? (side === "a" ? [v, row[1]] : [row[0], v]) : row));
  };
  return (
    <div className="py-1">
      <div className="text-[10px] uppercase tracking-widest text-jpa-muted mb-3 text-center">Type the final score of each game · Best of {bo}</div>
      <div className="space-y-2">
        {games.map((g, i) => (
          <div key={i} className="grid grid-cols-[auto_1fr_auto_1fr] gap-2 items-center">
            <span className="text-[11px] font-bold text-jpa-muted w-8">G{i + 1}</span>
            <Input data-testid={`manual-g${i}-a`} type="number" min={0} value={g[0]} onChange={(e) => setCell(i, "a", e.target.value)} className="rounded-sm h-11 text-center font-display text-xl" />
            <span className="text-jpa-muted">–</span>
            <Input data-testid={`manual-g${i}-b`} type="number" min={0} value={g[1]} onChange={(e) => setCell(i, "b", e.target.value)} className="rounded-sm h-11 text-center font-display text-xl" />
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-jpa-muted text-center">Leave 0-0 for games not played.</div>
      <div className="flex gap-2 justify-between mt-4">
        <Button data-testid="manual-cancel" variant="outline" onClick={onCancel} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><ChevronLeft className="w-3 h-3" /> Back to tap</Button>
        <Button data-testid="manual-save" onClick={() => onSave(games)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Save score</Button>
      </div>
    </div>
  );
}

// =========================================================================
// Referee Match Sheet — clean 4-step start flow:
//   1) Confirm A  →  2) Confirm B  →  3) Toss  →  4) Rules → Start
// =========================================================================
function MatchSheet({ match, onDone }) {
  const [m, setM] = useState(match);
  const [step, setStep] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  const [tossWinner, setTossWinner] = useState(null);
  const [woAsk, setWoAsk] = useState(false);
  const [injAsk, setInjAsk] = useState(false);
  const [flipped, setFlipped] = useState(false);   // UI-only: swap A/B display cards
  const [tick, setTick] = useState(0);
  const doubles = isDoubles(m);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isOnline()) {
          try {
            const r = await api.get(`/referee/matches`);
            const fresh = (r.data || []).find((x) => x.id === match.id) || match;
            if (!cancelled) { setM(fresh); saveCachedMatch(fresh); }
          } catch {
            const cached = getCachedMatch(match.id);
            if (cached && !cancelled) setM(cached);
          }
        } else {
          const cached = getCachedMatch(match.id) || match;
          if (!cancelled) setM(cached);
        }
      } finally {
        if (!cancelled) setStep(match.status === "completed" || match.status === "walkover" ? "done" : "confirm-a");
      }
    })();
    return () => { cancelled = true; };
  }, [match.id]);          // reset only when a DIFFERENT match opens

  useEffect(() => {
    const on = () => setTick((t) => t + 1);
    window.addEventListener("online", on); window.addEventListener("offline", on);
    const t = setInterval(on, 3000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", on); clearInterval(t); };
  }, []);

  const nameA = m.player_a_name || "TBD";
  const nameB = m.player_b_name || "TBD";
  const games = m.games?.length ? m.games : [[0, 0]];
  const curGame = m.live?.current_game ?? (games.length - 1);
  const cur = games[Math.min(curGame, games.length - 1)] || [0, 0];
  const serving = m.live?.serving_side;
  const bestOf = m.best_of || 1;
  const sideName = (s) => (s === "a" ? nameA : nameB);
  const label = `${ageLbl(m.age_category)} · ${EVT_LABELS[m.event_category] || m.event_category} · ${ROUND_LABELS[m.round] || m.round}${m.group_label ? ` · ${m.group_label}` : ""} · M${(m.order ?? 0) + 1}`;
  const completed = m.status === "completed" || m.status === "walkover";

  // ---- Actions (offline-safe) ----
  const doStart = async () => {
    setBusy(true);
    try {
      const r = await runAction({ mid: m.id, path: `/referee/matches/${m.id}/start`, body: null, localApply: localApplyStart });
      const next = r.match || { ...m, status: "in_progress" };
      setM(next); saveCachedMatch(next);
      setStep("play");
      if (r.offline) toast.info("Started (offline) — will sync when back online");
      else toast.success("Match started — LIVE");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRecordToss = async (choice) => {
    setBusy(true);
    try {
      const winner = tossWinner;
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/toss`, body: { winner_side: winner, choice },
        localApply: (mm) => localApplyToss(mm, winner, choice),
      });
      const next = r.match || { ...m, live: { ...(m.live || {}), toss: { winner_side: winner, choice }, initial_server: choice === "serve" ? winner : (winner === "a" ? "b" : "a"), serving_side: choice === "serve" ? winner : (winner === "a" ? "b" : "a"), current_game: 0, history: [] }, games: m.games?.length ? m.games : [[0, 0]] };
      setM(next); saveCachedMatch(next);
      toast.success(`Toss: ${sideName(winner)} · chose ${choice}`);
      setStep("rules");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doPoint = async (side) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/point`, body: { side },
        localApply: (mm) => localApplyPoint(mm, side),
      });
      const next = r.match || (r.data ? { ...m, games: r.data.games, status: r.data.status, winner_side: r.data.winner_side, live: { ...(m.live || {}), serving_side: r.data.serving_side, current_game: r.data.current_game, history: (m.live?.history || []).concat([1]) } } : m);
      setM(next); saveCachedMatch(next);
      if (next.status === "completed") { toast.success(`Match complete — ${sideName(next.winner_side)} wins`); onDone(); }
      if (r.offline) toast.info("Point recorded offline — will sync", { duration: 900 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doUndo = async () => {
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/undo`, body: null,
        localApply: localApplyUndo,
      });
      const next = r.match || (r.data ? { ...m, games: r.data.games, status: "in_progress", live: { ...(m.live || {}), serving_side: r.data.serving_side, current_game: r.data.current_game } } : m);
      setM(next); saveCachedMatch(next);
      toast.success("Last point undone");
    } catch (e) { toast.error(e.message || formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doWalkover = async (winner_side) => {
    if (!window.confirm(`${sideName(winner_side === "a" ? "b" : "a")} absent — ${sideName(winner_side)} wins 6-0 by walkover?`)) return;
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/walkover`, body: { winner_side },
        localApply: (mm) => localApplyWalkover(mm, winner_side),
      });
      const next = r.match || { ...m, status: "walkover", winner_side, games: winner_side === "a" ? [[6, 0]] : [[0, 6]] };
      setM(next); saveCachedMatch(next);
      setWoAsk(false);
      toast.success(`Walkover recorded · ${sideName(winner_side)} wins 6-0`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doBothAbsent = async () => {
    if (!window.confirm("Both sides absent — record 0-0 with NO winner?\nNeither side gets win points (walkover rule is NOT applied).")) return;
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/both-absent`, body: {},
        localApply: (mm) => ({ ...mm, status: "completed", winner_side: null, games: [[0, 0]], completion_reason: "both_absent" }),
      });
      const next = r.match || { ...m, status: "completed", winner_side: null, games: [[0, 0]], completion_reason: "both_absent" };
      setM(next); saveCachedMatch(next);
      setWoAsk(false);
      toast.success("Both absent · recorded 0-0, no winner");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doInjury = async (winner_side) => {
    if (!window.confirm(`${sideName(winner_side === "a" ? "b" : "a")} retires with injury — ${sideName(winner_side)} wins 11-0?`)) return;
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/injury`, body: { winner_side },
        localApply: (mm) => localApplyInjury(mm, winner_side),
      });
      const next = r.match || { ...m, status: "completed", winner_side, games: winner_side === "a" ? [[11, 0]] : [[0, 11]], completion_reason: "injury" };
      setM(next); saveCachedMatch(next);
      setInjAsk(false);
      toast.success(`Injury retirement · ${sideName(winner_side)} wins 11-0`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doTimeout = async (side) => {
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/timeout`, body: { side },
        localApply: (mm) => localApplyTimeout(mm, side),
      });
      const next = r.match || (r.data ? { ...m, live: { ...(m.live || {}), timeouts: r.data.timeouts } } : m);
      setM(next); saveCachedMatch(next);
      toast.success(`Timeout · ${sideName(side)}`);
    } catch (e) { toast.error(e.message || formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doSaveManual = async (typedGames) => {
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/score`, body: { games: typedGames },
        localApply: (mm) => localApplyScore(mm, typedGames),
      });
      const next = r.match || (r.data ? { ...m, games: typedGames, status: r.data.status, winner_side: r.data.winner_side } : m);
      setM(next); saveCachedMatch(next);
      setManual(false);
      if (next.status === "completed") { toast.success(`Match saved — ${sideName(next.winner_side)} wins`); onDone(); }
      else toast.success("Score saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doUndoCompletion = async () => {
    if (!window.confirm(`Undo this ${m.status === "walkover" ? "walkover" : "result"}?\nMatch returns to Scheduled — you'll re-do toss & scoring.\n(If the winner was already advanced to a knockout match, the organizer must fix that manually.)`)) return;
    setBusy(true);
    try {
      const r = await runAction({
        mid: m.id, path: `/referee/matches/${m.id}/undo-completion`, body: null,
        localApply: localApplyUndoCompletion,
      });
      const next = r.match || { ...m, status: "scheduled", games: [], live: {}, winner_side: undefined, completed_at: undefined, completion_reason: undefined };
      setM(next); saveCachedMatch(next);
      setStep("confirm-a");
      toast.success("Match reset to Scheduled");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // -------------------- Shared chrome --------------------
  const headerBar = (
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-widest text-jpa-primary font-bold truncate">{label}</div>
      <div className="flex items-center gap-1">
        {(m.status === "in_progress") && (
          <Button data-testid="ref-flip-sides" onClick={() => setFlipped((v) => !v)} variant="outline" size="sm" className={`h-7 rounded-sm text-[9px] uppercase tracking-widest font-bold gap-1 px-2 ${flipped ? "border-jpa-primary bg-jpa-surface text-jpa-primary" : ""}`} title="Flip left/right sides to match court positions">
            <ArrowLeftRight className="w-3 h-3" /> Flip
          </Button>
        )}
        {isOnline() ? <Wifi className="w-3.5 h-3.5 text-emerald-600" /> : <WifiOff className="w-3.5 h-3.5 text-amber-600" />}
        {pendingCount() > 0 && <Badge data-testid="ref-queue-count" className="bg-amber-500 text-white rounded-sm text-[9px] font-bold px-1.5">{pendingCount()}↑</Badge>}
      </div>
    </div>
  );
  const stepBadge = (n) => <div className="text-[10px] uppercase tracking-widest text-jpa-muted text-center mb-2">Step {n} of 4</div>;

  if (step === "loading") return <div className="py-10 text-center text-sm text-jpa-muted">Loading match…</div>;

  // -------------------- Done --------------------
  if (completed || step === "done") {
    return (
      <div className="py-2">
        {headerBar}
        <div className="text-center py-4 border-2 border-emerald-500 rounded-sm bg-emerald-50">
          <Lock className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
          <div className="font-display text-xl tracking-wider text-emerald-700">{m.completion_reason === "both_absent" ? "BOTH ABSENT — 0-0, NO WINNER" : `${sideName(m.winner_side)} WINS${m.status === "walkover" ? " (Walkover)" : m.completion_reason === "injury" ? " (Injury Retirement)" : ""}`}</div>
          <div className="text-sm text-jpa-muted mt-1">{games.filter((g) => g[0] !== 0 || g[1] !== 0).map((g) => `${g[0]}-${g[1]}`).join("  ·  ")}</div>
          <Button data-testid="ref-undo-completion" onClick={doUndoCompletion} disabled={busy} variant="outline" size="sm" className="mt-3 rounded-sm text-[11px] uppercase tracking-widest font-bold gap-1 border-amber-500 text-amber-700 hover:bg-amber-100"><Undo2 className="w-3 h-3" /> Undo result</Button>
          <div className="mt-2 text-[10px] text-jpa-muted uppercase tracking-widest">Undo resets to Scheduled. Bracket rollback is manual — organizer edits the next match.</div>
        </div>
      </div>
    );
  }

  // -------------------- STEP 1: confirm A --------------------
  if (step === "confirm-a") {
    return (
      <div className="py-2">
        {headerBar}
        {stepBadge(1)}
        <div className="text-xs font-bold uppercase tracking-widest text-center mb-2">Confirm {doubles ? "Team A" : "Player A"}</div>
        <TeamCard name={nameA} doubles={doubles} tone="primary" size="xl" />
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button data-testid="ref-confirm-a-no" variant="outline" onClick={onDone} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><XCircle className="w-3 h-3" /> Wrong match</Button>
          <Button data-testid="ref-confirm-a-yes" onClick={() => setStep("confirm-b")} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold gap-1">Next <ChevronRight className="w-3 h-3" /></Button>
        </div>
      </div>
    );
  }

  // -------------------- STEP 2: confirm B --------------------
  if (step === "confirm-b") {
    return (
      <div className="py-2">
        {headerBar}
        {stepBadge(2)}
        <div className="text-xs font-bold uppercase tracking-widest text-center mb-2">Confirm {doubles ? "Team B" : "Player B"}</div>
        <TeamCard name={nameB} doubles={doubles} tone="primary" size="xl" />
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button data-testid="ref-confirm-b-back" variant="outline" onClick={() => setStep("confirm-a")} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><ChevronLeft className="w-3 h-3" /> Back</Button>
          <Button data-testid="ref-confirm-b-yes" onClick={() => setStep(m.live?.serving_side ? "rules" : "toss")} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold gap-1">Next <ChevronRight className="w-3 h-3" /></Button>
        </div>
      </div>
    );
  }

  // -------------------- STEP 3: toss (player-called) --------------------
  if (step === "toss") {
    return (
      <div className="py-2">
        {headerBar}
        {stepBadge(3)}
        {!tossWinner ? (
          <>
            <div className="text-sm text-center mb-3 font-bold">Toss called by players — who won?</div>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="ref-toss-winner-a" onClick={() => setTossWinner("a")} className="h-auto py-2 bg-white border-2 border-jpa-border hover:border-jpa-primary hover:bg-jpa-surface text-jpa-ink rounded-sm">
                <TeamCard name={nameA} doubles={doubles} size="md" />
              </Button>
              <Button data-testid="ref-toss-winner-b" onClick={() => setTossWinner("b")} className="h-auto py-2 bg-white border-2 border-jpa-border hover:border-jpa-primary hover:bg-jpa-surface text-jpa-ink rounded-sm">
                <TeamCard name={nameB} doubles={doubles} size="md" />
              </Button>
            </div>
            <Button data-testid="ref-toss-back" variant="outline" onClick={() => setStep("confirm-b")} className="mt-3 w-full rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><ChevronLeft className="w-3 h-3" /> Back</Button>
          </>
        ) : (
          <>
            <div className="text-center mb-3">
              <div className="text-[10px] uppercase tracking-widest text-jpa-muted">Toss winner</div>
              <div className="font-display text-xl tracking-wider text-jpa-primary mt-1">{sideName(tossWinner)}</div>
            </div>
            <div className="text-sm text-center mb-3 font-bold">What did they choose?</div>
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="ref-toss-choice-serve" disabled={busy} onClick={() => doRecordToss("serve")} className="h-14 bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm font-bold uppercase tracking-widest">Serve first</Button>
              <Button data-testid="ref-toss-choice-side" disabled={busy} onClick={() => doRecordToss("side")} variant="outline" className="h-14 rounded-sm font-bold uppercase tracking-widest">Choose side</Button>
            </div>
            <Button data-testid="ref-toss-reselect" variant="outline" onClick={() => setTossWinner(null)} className="mt-3 w-full rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><ChevronLeft className="w-3 h-3" /> Wrong winner? Re-select</Button>
          </>
        )}
      </div>
    );
  }

  // -------------------- STEP 4: rules card → start --------------------
  if (step === "rules") {
    const rules = [
      { k: "Scoring", v: m.game_type === "side_out" ? "Side-out (traditional)" : "Rally scoring" },
      { k: "Sets", v: `Best of ${bestOf}` },
      { k: "Points per game", v: String(m.points_per_game || 11) },
      { k: "Win by", v: String(m.win_by || 2) },
    ];
    return (
      <div className="py-2">
        {headerBar}
        {stepBadge(4)}
        <div className="text-xs font-bold uppercase tracking-widest text-center mb-2 flex items-center justify-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-jpa-primary" /> Match rules (set by organizer)</div>
        <div className="grid grid-cols-2 gap-2">
          {rules.map((r) => (
            <div key={r.k} className="border border-jpa-border rounded-sm p-3 text-center bg-white">
              <div className="text-[9px] uppercase tracking-widest text-jpa-muted font-bold">{r.k}</div>
              <div className="font-display text-xl tracking-wider mt-0.5">{r.v}</div>
            </div>
          ))}
        </div>
        {m.live?.serving_side && (
          <div className="mt-2 text-center text-[11px] text-jpa-muted border border-jpa-border rounded-sm px-3 py-2 bg-jpa-surface">
            Toss done — <b className="text-jpa-primary">{sideName(m.live.serving_side)}</b> serves first
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button data-testid="ref-rules-back" variant="outline" onClick={() => setStep(m.live?.serving_side ? "confirm-b" : "toss")} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><ChevronLeft className="w-3 h-3" /> Back</Button>
          {m.status === "in_progress" ? (
            <Button data-testid="ref-rules-continue" onClick={() => setStep("play")} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold gap-1">Continue scoring <ChevronRight className="w-3 h-3" /></Button>
          ) : (
            <Button data-testid="ref-start-btn" onClick={doStart} disabled={busy} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold gap-1"><Play className="w-3 h-3" /> Start match</Button>
          )}
        </div>
      </div>
    );
  }

  // -------------------- Scoring (play) --------------------
  if (manual) return <div>{headerBar}<ManualScoreGrid match={m} onSave={doSaveManual} onCancel={() => setManual(false)} /></div>;

  return (
    <div className="py-1">
      {headerBar}
      {m.status === "scheduled" ? (
        <div className="text-center">
          <Button data-testid="ref-start-btn-play" onClick={doStart} disabled={busy} className="w-full bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm font-bold uppercase tracking-widest h-12 gap-2"><Play className="w-4 h-4" /> Start match</Button>
          <Button data-testid="ref-play-back" variant="outline" onClick={() => setStep("rules")} className="mt-3 rounded-sm text-xs uppercase tracking-widest font-bold gap-1 w-full"><ChevronLeft className="w-3 h-3" /> Back to rules</Button>
        </div>
      ) : (
        <div>
          <div className="text-center text-[10px] uppercase tracking-widest text-jpa-muted mb-2">Game {curGame + 1} of {bestOf} · {m.points_per_game}pt · win-by-{m.win_by}</div>
          <div className="grid grid-cols-2 gap-3">
            {(flipped ? ["b", "a"] : ["a", "b"]).map((side) => {
              const isServing = serving === side;
              const timeoutsUsed = !!((m.live?.timeouts || {})[String(curGame)] || {})[side];
              return (
                <div key={side} className={`border-2 rounded-sm p-3 text-center transition-colors ${isServing ? "border-jpa-accent bg-jpa-accent/10" : "border-jpa-border"}`}>
                  <TeamCard name={sideName(side)} doubles={doubles} size="md" />
                  {isServing && <Badge data-testid={`ref-serving-${side}`} className="bg-jpa-accent text-jpa-ink rounded-sm text-[9px] font-bold uppercase tracking-widest mt-1">● Serving</Badge>}
                  <div data-testid={`ref-score-${side}`} className="font-display text-6xl tracking-wider my-2">{cur[side === "a" ? 0 : 1]}</div>
                  <Button data-testid={`ref-point-${side}`} onClick={() => doPoint(side)} disabled={busy} className="w-full bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm font-bold uppercase tracking-widest h-14 text-lg">+1</Button>
                  <Button data-testid={`ref-timeout-${side}`} onClick={() => doTimeout(side)} disabled={busy || timeoutsUsed} variant="outline" size="sm" className={`w-full mt-2 rounded-sm text-[10px] uppercase tracking-widest font-bold gap-1 ${timeoutsUsed ? "opacity-40" : ""}`}>
                    <Timer className="w-3 h-3" /> {timeoutsUsed ? "Timeout used" : "Timeout (1)"}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* ---- tidy bottom bar: chips + 3 compact actions ---- */}
          <div className="flex items-center justify-center gap-1 mt-3 overflow-x-auto">
            {games.map((g, i) => (
              <span key={i} className={`text-[11px] font-mono font-bold px-2 py-1 rounded-sm shrink-0 ${i === curGame ? "bg-jpa-primary text-white" : "bg-jpa-surface text-jpa-muted"}`}>G{i + 1} {g[0]}-{g[1]}</span>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Button data-testid="ref-undo" onClick={doUndo} disabled={busy} variant="outline" size="sm" className="rounded-sm text-[11px] uppercase tracking-widest font-bold gap-1"><Undo2 className="w-3.5 h-3.5" /> Undo</Button>
            <Button data-testid="ref-manual" onClick={() => setManual(true)} variant="outline" size="sm" className="rounded-sm text-[11px] uppercase tracking-widest font-bold gap-1"><FileText className="w-3.5 h-3.5" /> Manual</Button>
            <Button data-testid="ref-injury" onClick={() => { setInjAsk((v) => !v); setWoAsk(false); }} variant="outline" size="sm" className={`rounded-sm text-[11px] uppercase tracking-widest font-bold gap-1 ${injAsk ? "border-red-500 bg-red-50 text-red-700" : ""}`}><HeartCrack className="w-3.5 h-3.5" /> Injury</Button>
            <Button data-testid="ref-walkover" onClick={() => { setWoAsk((v) => !v); setInjAsk(false); }} variant="outline" size="sm" className={`rounded-sm text-[11px] uppercase tracking-widest font-bold gap-1 ${woAsk ? "border-amber-500 bg-amber-50 text-amber-700" : ""}`}><XCircle className="w-3.5 h-3.5" /> W/O</Button>
          </div>
          {woAsk && (
            <div data-testid="ref-walkover-panel" className="mt-2 border border-amber-300 bg-amber-50 rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 text-center mb-2">Walkover: which side is PRESENT? (absent side loses 6-0)</div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="ref-walkover-a" onClick={() => doWalkover("a")} disabled={busy} size="sm" className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold uppercase tracking-widest">A present</Button>
                <Button data-testid="ref-walkover-b" onClick={() => doWalkover("b")} disabled={busy} size="sm" className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold uppercase tracking-widest">B present</Button>
              </div>
              {m.round === "group" && (
                <Button data-testid="ref-both-absent" onClick={doBothAbsent} disabled={busy} size="sm" className="mt-2 w-full rounded-sm bg-slate-600 hover:bg-slate-700 text-white text-[11px] font-bold uppercase tracking-widest">Both absent — 0-0, no winner</Button>
              )}
            </div>
          )}
          {injAsk && (
            <div data-testid="ref-injury-panel" className="mt-2 border border-red-300 bg-red-50 rounded-sm p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-red-800 text-center mb-2">Injury retirement: which side is HEALTHY? (injured side loses 11-0)</div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="ref-injury-a" onClick={() => doInjury("a")} disabled={busy} size="sm" className="rounded-sm bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold uppercase tracking-widest">A healthy</Button>
                <Button data-testid="ref-injury-b" onClick={() => doInjury("b")} disabled={busy} size="sm" className="rounded-sm bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold uppercase tracking-widest">B healthy</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Referee Hub — filters + list + offline sync indicator
// =========================================================================
export default function RefereeDashboardPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [fAge, setFAge] = useState("all");
  const [fEvent, setFEvent] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [onlineTick, setOnlineTick] = useState(0);

  const load = () => api.get("/referee/matches").then((r) => {
    setMatches(r.data);
    (r.data || []).forEach(saveCachedMatch);
  }).catch(() => {});

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/referee/login"); return; }
    if (user.role !== "referee") { nav("/"); return; }
    load();
    const t = setInterval(() => { load(); flushQueue(); }, 20000);
    return () => clearInterval(t);
  }, [user, loading, nav]);

  useEffect(() => {
    const on = () => setOnlineTick((x) => x + 1);
    window.addEventListener("online", on); window.addEventListener("offline", on);
    const t = setInterval(on, 3000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", on); clearInterval(t); };
  }, []);

  const ageOptions = useMemo(() => [...new Set(matches.map((m) => m.age_category).filter(Boolean))].sort(), [matches]);
  const eventOptions = useMemo(() => [...new Set(matches.map((m) => m.event_category).filter(Boolean))].sort(), [matches]);
  const filtered = useMemo(() => matches.filter((m) =>
    (fAge === "all" || m.age_category === fAge) &&
    (fEvent === "all" || m.event_category === fEvent) &&
    (fStatus === "all" || m.status === fStatus)
  ), [matches, fAge, fEvent, fStatus]);
  const online = isOnline();
  const queue = pendingCount();

  return (
    <div data-testid="referee-dashboard-page" className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 text-jpa-primary" />
          <h1 className="font-display text-4xl tracking-wider">REFEREE HUB</h1>
        </div>
        <div className="flex items-center gap-2">
          {online ? (
            <Badge data-testid="ref-hub-online" className="bg-emerald-600 text-white rounded-sm text-[10px] font-bold uppercase tracking-widest gap-1"><Wifi className="w-3 h-3" /> Online</Badge>
          ) : (
            <Badge data-testid="ref-hub-offline" className="bg-amber-500 text-white rounded-sm text-[10px] font-bold uppercase tracking-widest gap-1"><WifiOff className="w-3 h-3" /> Offline · scoring still works</Badge>
          )}
          {queue > 0 && <Badge data-testid="ref-hub-queue" className="bg-amber-600 text-white rounded-sm text-[10px] font-bold uppercase tracking-widest gap-1"><CheckCircle2 className="w-3 h-3" /> {queue} to sync</Badge>}
        </div>
      </div>
      <p className="text-sm text-jpa-muted mb-4">Open each match once while online — it will keep scoring even if the internet drops.</p>

      <div className="grid grid-cols-3 gap-2 mb-6 max-w-2xl">
        <Select value={fAge} onValueChange={setFAge}>
          <SelectTrigger data-testid="ref-filter-age" className="rounded-sm h-10 bg-white text-xs"><SelectValue placeholder="Age" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            {ageOptions.map((a) => <SelectItem key={a} value={a}>{ageLbl(a)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fEvent} onValueChange={setFEvent}>
          <SelectTrigger data-testid="ref-filter-event" className="rounded-sm h-10 bg-white text-xs"><SelectValue placeholder="Event" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {eventOptions.map((e) => <SelectItem key={e} value={e}>{EVT_LABELS[e] || e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="ref-filter-status" className="rounded-sm h-10 bg-white text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="scheduled">Upcoming</SelectItem>
            <SelectItem value="in_progress">Live</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 && <div className="border border-dashed border-jpa-border rounded-sm p-16 text-center text-jpa-muted">No matches for this filter.</div>}
      <div className="space-y-3">
        {filtered.map((m) => {
          const doubles = isDoubles(m);
          return (
            <Dialog key={m.id}>
              <DialogTrigger asChild>
                <div data-testid={`ref-match-${m.id}`} className="cursor-pointer border border-jpa-border rounded-sm p-4 hover:bg-jpa-surface transition-colors">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-jpa-muted mb-1">
                        <Badge className="bg-jpa-primary text-white rounded-sm text-[9px] mr-2">{ROUND_LABELS[m.round]}</Badge>
                        <Badge variant="outline" className="rounded-sm text-[9px]">{EVT_LABELS[m.event_category]}</Badge>
                        <Badge variant="outline" className="rounded-sm text-[9px] ml-1">{ageLbl(m.age_category)}</Badge>
                        {m.group_label && <Badge variant="outline" className="rounded-sm text-[9px] ml-1">{m.group_label}</Badge>}
                        {m.scheduled_time && <span className="ml-2">{m.scheduled_day} · {m.scheduled_time} · Court {m.court}</span>}
                      </div>
                      <div className="font-bold text-sm">
                        <span className={m.winner_side === "a" ? "text-emerald-700" : ""}>{doubles ? (splitDoubles(m.player_a_name)[0] + (splitDoubles(m.player_a_name)[1] ? " / " + splitDoubles(m.player_a_name)[1] : "")) : (m.player_a_name || "TBD")}</span>
                        <span className="mx-2 text-jpa-muted">vs</span>
                        <span className={m.winner_side === "b" ? "text-emerald-700" : ""}>{doubles ? (splitDoubles(m.player_b_name)[0] + (splitDoubles(m.player_b_name)[1] ? " / " + splitDoubles(m.player_b_name)[1] : "")) : (m.player_b_name || "TBD")}</span>
                      </div>
                      {m.games?.length > 0 && (
                        <div className="text-xs text-jpa-muted mt-1">{m.games.map((g) => `${g[0]}-${g[1]}`).join(" · ")}</div>
                      )}
                    </div>
                    <Badge className={`rounded-sm text-[10px] ${m.status === "completed" || m.status === "walkover" ? "bg-emerald-500" : m.status === "in_progress" ? "bg-amber-500" : "bg-slate-400"} text-white`}>{m.status}</Badge>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-md">
                <DialogHeader><DialogTitle>Match Sheet · M{(m.order ?? 0) + 1}</DialogTitle></DialogHeader>
                <MatchSheet match={m} onDone={load} />
              </DialogContent>
            </Dialog>
          );
        })}
      </div>
    </div>
  );
}
